import { clampLearningValue, type LearningStatus } from './learning-state-core'

export type ProgressBadgeVariant =
  | 'outline'
  | 'secondary'
  | 'success'
  | 'neutral'

export type ProgressStateLike = {
  status?: LearningStatus | string | null
  progress?: number | null
}

export type StoryProgressKind = 'new' | 'started' | 'read'
export type GrammarProgressKind = 'new' | 'seen' | 'studying' | 'learned'

export type ProgressDisplay = {
  kind: StoryProgressKind | GrammarProgressKind
  label: string
  badgeVariant: ProgressBadgeVariant
}

export function isStoryRead(state: ProgressStateLike | null | undefined) {
  return state?.status === 'completed'
}

export function isStoryStarted(state: ProgressStateLike | null | undefined) {
  if (isStoryRead(state)) return false
  return (
    state?.status === 'studying' ||
    (clampLearningValue(state?.progress) ?? 0) > 0
  )
}

export function getStoryProgressKind(
  state: ProgressStateLike | null | undefined
): StoryProgressKind {
  if (isStoryRead(state)) return 'read'
  if (isStoryStarted(state)) return 'started'
  return 'new'
}

export function getStoryProgressDisplay(
  state: ProgressStateLike | null | undefined
): ProgressDisplay | null {
  const kind = getStoryProgressKind(state)
  if (kind === 'read') {
    return { kind, label: 'Read', badgeVariant: 'success' }
  }
  if (kind === 'started') {
    return { kind, label: 'Started', badgeVariant: 'outline' }
  }
  return null
}

export function getGrammarProgressKind(
  state: ProgressStateLike | null | undefined
): GrammarProgressKind {
  if (state?.status === 'learned' || state?.status === 'completed') {
    return 'learned'
  }
  if (state?.status === 'studying') return 'studying'
  if (state?.status === 'seen' || state?.status === 'saved') return 'seen'
  return 'new'
}

export function getGrammarProgressDisplay(
  state: ProgressStateLike | null | undefined
): ProgressDisplay {
  const kind = getGrammarProgressKind(state)
  if (kind === 'learned') {
    return { kind, label: 'Learned', badgeVariant: 'success' }
  }
  if (kind === 'studying') {
    return { kind, label: 'Studying', badgeVariant: 'secondary' }
  }
  if (kind === 'seen') {
    return { kind, label: 'Seen', badgeVariant: 'outline' }
  }
  return { kind, label: 'New', badgeVariant: 'neutral' }
}

export function progressPercent(value: number | null | undefined) {
  const clamped = clampLearningValue(value)
  return clamped === null ? null : Math.round(clamped * 100)
}
